import { Dispatch } from 'redux';
import { KEEP_ALIVE_FAILED, KEEP_ALIVE_SUCCESS } from './types';
import axios from 'axios';
import { time } from 'uniqid';
import { ReduxStore } from '../payload-types';
import { get } from 'lodash';
import { LINKS } from '@trip-ux/constants';
import { deferUntilIdle } from '@trip-ux/common';
import { getCovermeFlag } from '../coverme-flags';

export const refreshSessionCounter = (dispatch: Dispatch): void => {
  dispatch({ type: KEEP_ALIVE_SUCCESS, payload: time('') });
};

// R12 — Move keepalive off the interaction critical path via
// `requestIdleCallback`.
//
// `keepingAlive` and `keepingAliveAdmin` fire `axios.get('/keepalive')` on
// every user activity (throttled ~3 s). Real-user data: 17,679 calls/day on
// plan-selection alone. Each axios.get() allocates an XHR slot on the main
// thread, blocking user interactions for a few ms and hurting INP p75.
// See RCA § 1.9 and plan Task 12.
//
// When the runtime flag `r12KeepaliveBeacon` is on, the axios call is
// deferred via `deferUntilIdle` (PA-12 helper). The browser fires it during
// the next idle window (or after the 4 s timeout safety net), off the
// interaction path. Return value semantics are preserved — the Promise
// still resolves only when the axios round-trip completes, so callers
// awaiting the keepalive continue to work unchanged.
//
// Fallback path (`deferUntilIdle` with no `requestIdleCallback` support)
// uses `setTimeout` which is still off the immediate interaction task,
// so the win is preserved on Safari < 16.4 as well.
//
// Flag is sourced from `window.__COVERME_FLAGS__.r12KeepaliveBeacon` at
// runtime (server-injected via booking.controller.ts render context) with
// a `process.env.COVERME_R12_KEEPALIVE_BEACON` fallback for Node/SSR and
// unit tests. See libs/redux-store/src/coverme-flags.ts. Default off.
// Flip per environment via chart values.
const runKeepAliveWork = (work: () => Promise<void>): Promise<void> => {
  if (!getCovermeFlag('r12KeepaliveBeacon')) {
    return work();
  }
  return new Promise<void>((resolve) => {
    deferUntilIdle(() => {
      work().finally(() => resolve());
    });
  });
};

export const keepingAlive =
  () =>
  (dispatch: Dispatch, getState: () => ReduxStore): Promise<void> =>
    runKeepAliveWork(() =>
      axios
        .get(`${get(getState(), 'config.options.contextPath', '')}${LINKS.KEEP_ALIVE}`)
        .then(response => {
          dispatch({ type: KEEP_ALIVE_SUCCESS, payload: get(response, 'data.sessionExpiration') });
        })
        .catch(err => {
          dispatch({ type: KEEP_ALIVE_FAILED, payload: err });
        })
    );

export const keepingAliveAdmin =
  () =>
  (dispatch: Dispatch, _getState: () => ReduxStore): Promise<void> =>
    runKeepAliveWork(() =>
      axios
        .get('/keepalive')
        .then(response => {
          // Sync CSRF token if the BFF returned one (keeps frontend in sync after session rotation)
          const csrfToken = get(response, 'data.csrfToken');
          if (csrfToken) {
            axios.defaults.headers.common['x-csrf-token'] = csrfToken;
          }
          dispatch({ type: KEEP_ALIVE_SUCCESS, payload: get(response, 'data.sessionExpiration') });
        })
        .catch(err => {
          dispatch({ type: KEEP_ALIVE_FAILED, payload: err });
        })
    );
